import { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TONE = { done: "text-emerald-300", processing: "text-amber-300", failed: "text-rose-300", legacy: "text-slate-400" };
const ago = (s) => (s == null ? "—" : s < 90 ? `${s}s` : s < 5400 ? `${Math.round(s / 60)}m` : s < 172800 ? `${Math.round(s / 3600)}h` : `${Math.round(s / 86400)}d`);

const Stat = ({ label, value, tone = "", tid }) => (
  <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-3" data-testid={tid}>
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`text-xl font-display font-bold mt-0.5 ${tone}`}>{value ?? "—"}</div>
  </div>
);

const Row = ({ r, i, prefix }) => (
  <tr className="border-t border-slate-800/60" data-testid={`${prefix}-${i}`}>
    <td className={`py-1.5 pr-3 font-semibold ${TONE[r.status]}`}>{r.status}{r.lease_expired ? " · lease expired" : ""}</td>
    <td className="py-1.5 pr-3 text-slate-200">{r.type}</td>
    <td className="py-1.5 pr-3 text-slate-500 font-mono-ticker text-[11px]">{r.id}</td>
    <td className="py-1.5 pr-3 text-slate-400">{ago(r.age_s)}</td>
    <td className="py-1.5 pr-3 text-slate-400">{r.attempts ?? "—"}{r.retry_expected ? <span className="text-amber-300"> · Stripe retry expected</span> : ""}</td>
    <td className="py-1.5 pr-3 text-slate-400">{r.duration_s != null ? `${r.duration_s}s` : "—"}</td>
    <td className="py-1.5 text-slate-400 max-w-[22rem] truncate" title={r.error || ""}>{r.error || ""}</td>
  </tr>
);

export function WebhookHealthCard() {
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setRes((await api.get("/admin/billing/webhook-health")).data); }
    catch (e) { toast.error(apiError(e, "Webhook health unavailable")); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (user?.is_admin) load(); }, [user?.is_admin, load]);
  if (!user?.is_admin) return null;
  const c = res?.counts, s = c?.by_status || {};
  const attention = res?.attention?.length || 0;
  return (
    <section className="apx-card p-5 space-y-3" data-testid="webhook-health-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-300" /> Stripe webhooks — processing health</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">Every Stripe event is claimed, processed, then marked <em>done</em>. Failed or interrupted events stay visible here until Stripe's automatic retry reprocesses them (lease {res?.lease_seconds ?? 600}s). Event ids and types only — no card, amount or customer data.</p>
        </div>
        <button type="button" onClick={load} disabled={busy} data-testid="webhook-health-refresh-btn" className="btn-ghost border border-slate-700 rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1.5 shrink-0">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh</button>
      </div>
      {res && (
        <div className="space-y-3" data-testid="webhook-health-results">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Stat label="Needs attention" value={attention} tone={attention ? "text-rose-300" : "text-emerald-300"} tid="wh-attention" />
            <Stat label="Failed" value={s.failed || 0} tone={s.failed ? "text-rose-300" : ""} tid="wh-failed" />
            <Stat label="Processing" value={s.processing || 0} tone={s.processing ? "text-amber-300" : ""} tid="wh-processing" />
            <Stat label="Done" value={s.done || 0} tid="wh-done" />
            <Stat label="Retried" value={c.retried} tid="wh-retried" />
            <Stat label="Last 24h / 7d" value={`${c.last_24h} / ${c.last_7d}`} tid="wh-volume" />
            <Stat label="Max / avg duration" value={res.duration.max_s != null ? `${res.duration.max_s}s / ${res.duration.avg_s}s` : "—"} tid="wh-duration" />
          </div>
          <div className="text-xs text-slate-500" data-testid="wh-meta">{s.legacy || 0} events predate status tracking · {c.unhandled_done_7d} unhandled-type events in 7d (marked done, no action) · billing notifications {res.notifications.billing_total} ({res.notifications.deduped_keys} de-duplicated by invoice) · handled: {res.handled_types.join(", ")}</div>
          <div className="tbl-scroll overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Event</th><th className="py-1 pr-3">Age</th><th className="py-1 pr-3">Attempts</th><th className="py-1 pr-3">Duration</th><th className="py-1">Error</th></tr></thead>
              <tbody>
                {attention === 0 && <tr data-testid="wh-attention-empty"><td colSpan={7} className="py-2 text-emerald-300/90">No failed or in-flight events — nothing is stuck.</td></tr>}
                {res.attention.map((r, i) => <Row key={r.id} r={r} i={i} prefix="wh-attention-row" />)}
                {res.recent_done.map((r, i) => <Row key={r.id} r={r} i={i} prefix="wh-done-row" />)}
                {res.recent_done.length === 0 && <tr data-testid="wh-done-empty"><td colSpan={7} className="py-2 text-slate-500">No events processed by the current webhook code yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
